/**
 * Datumsangaben auf Deutsch.
 *
 * ## Warum das an einer Stelle steht
 *
 * Vorher schnitt jede Anzeigestelle sich ihr Datum selbst aus dem Zeitstempel
 * (`ts.slice(0, 10)`) — und zeigte damit die Speicherform: `2026-08-03`. Das
 * ist maschinenlesbar und für den Leser die falsche Reihenfolge. Es stand an
 * fünf Stellen, in vier Dateien, jedes Mal anders.
 *
 * ## Zerlegt, nicht gerechnet
 *
 * Dieselbe Entscheidung wie in `tagebuch.ts`, und aus demselben Grund: Die
 * Zeitstempel kommen ohne Zeitzone. `new Date('2026-08-03')` liest die
 * Zeichenkette als UTC-Mitternacht — wer westlich von Greenwich sitzt, sieht
 * dann den 2. August. Ein Datum ist hier ein **Kalendertag**, kein Zeitpunkt,
 * und wird deshalb zerlegt statt umgerechnet.
 */

/** `2026-08-03T18:30:00` → `[2026, 8, 3]`, oder `null` wenn nichts brauchbar ist. */
function teile(iso: string | null | undefined): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** `2026-08-03` → `03.08.2026`. Das Standardformat nach DIN 5008. */
export function datum(iso: string | null | undefined): string {
  const t = teile(iso);
  if (!t) return '';
  const [j, m, d] = t;
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${j}`;
}

/** `2026-08-03T18:30:11` → `03.08.2026, 18:30`. Sekunden interessieren nie. */
export function datumZeit(iso: string | null | undefined): string {
  const d = datum(iso);
  if (!d) return '';
  const uhr = (iso ?? '').slice(11, 16);
  return uhr ? `${d}, ${uhr}` : d;
}

/**
 * Ein **Server-Zeitstempel** in der Ortszeit des Lesers:
 * `2026-09-20T14:05:11` → `20.09.2026, 16:05` (in Berlin, im Sommer).
 *
 * ## Warum das nicht `datumZeit` ist
 *
 * `datumZeit` zerlegt die Zeichenkette und rechnet nicht — richtig für alles,
 * was eine Wanduhrzeit **ist** (Ankunft, Tagebuch, Abfahrt). Die Zeitstempel
 * der Buchführung dagegen (`last_seen_at` der Freigaben, der Sitzungen)
 * schreibt der Server mit `UTC_TIMESTAMP()` und schickt sie **ohne**
 * Zonenangabe. Zerlegt stünde dort die Uhr von Greenwich: im Sommer zwei
 * Stunden zu früh, und kurz nach Mitternacht sogar der falsche Tag. Florian
 * (20.9.2026, unterwegs): „Kannst Du auf der Seite Teilen auch die Uhrzeit
 * anzeigen bei zuletzt gelesen?“ — die Uhrzeit ist erst dann eine Auskunft,
 * wenn sie die eigene ist.
 *
 * Eine Angabe **mit** Zone (`Z`, `+02:00`) wird so genommen, wie sie dasteht;
 * nur die nackte gilt als UTC. `zone` ist für die Tests da — ohne sie gilt
 * die Zone des Browsers, und das ist die des Lesers.
 */
export function zeitpunktHier(iso: string | null | undefined, zone?: string): string {
  const roh = (iso ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(roh)) return datum(roh);
  const mitT = roh.replace(' ', 'T');
  const hatZone = /(Z|[+-]\d{2}:?\d{2})$/.test(mitT);
  const d = new Date(hatZone ? mitT : `${mitT}Z`);
  if (Number.isNaN(d.getTime())) return datum(roh);
  const stuecke = new Intl.DateTimeFormat('de-DE', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(d);
  const wert = (art: string) => stuecke.find((x) => x.type === art)?.value ?? '';
  return `${wert('day')}.${wert('month')}.${wert('year')}, ${wert('hour')}:${wert('minute')}`;
}

/**
 * Ein Zeitraum: `03.08.2026 – 10.08.2026`.
 *
 * Numerisch und in voller Länge auf beiden Seiten — nichts wird
 * zusammengezogen. Ein Zeitraum wird abgelesen, und beim Ablesen ist eine
 * Angabe, die auf ihrer Nachbarin aufbaut („3. bis 10. August"), einen
 * Gedankenschritt teurer als zwei vollständige.
 *
 * Der Halbgeviertstrich mit Leerzeichen ist der übliche Bis-Strich; ein
 * Bindestrich wäre falsch und ohne Leerzeichen klebten die Ziffernblöcke
 * aneinander.
 *
 * Ein einzelner Tag steht einmal da, nicht zweimal.
 */
export function zeitraum(
  von: string | null | undefined,
  bis: string | null | undefined
): string {
  const a = datum(von);
  const b = datum(bis);
  if (!a && !b) return '';
  if (!a) return `bis ${b}`;
  if (!b) return `ab ${a}`;
  if (a === b) return a;
  return `${a} – ${b}`;
}

/**
 * Die deutschen Monatsnamen, Januar bei Index 1.
 *
 * Ausgeschrieben und nicht über `Intl`: Die Liste ist zwölf Wörter lang und
 * ändert sich nie, und `Intl` bringt die Sprache des **Browsers** mit — auf
 * einem englisch eingestellten Rechner stünde dann „September 2026" neben
 * „14.09.2026", also zwei Sprachen in einer Zeile. Diese Anwendung ist
 * deutsch, an jeder Stelle und unabhängig vom Gerät.
 */
const MONATE = [
  '',
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember'
];

/**
 * `2026-09-14` → `September 2026`.
 *
 * Für Listen, die nach Monat gruppiert sind — das Handbuch ordnet „Was ist
 * neu" so. Der Monatsname trägt die Überschrift, weil eine Reihe aus
 * `2026-09` darüber die Speicherform zeigte.
 */
export function monatJahr(iso: string | null | undefined): string {
  const t = teile(iso);
  if (!t) return '';
  const [j, m] = t;
  return `${MONATE[m] ?? ''} ${j}`.trim();
}
